import React, { useState } from "react";
import { CreateCommanWay, ListCommanWay } from "API/commonApi";
import { useMutation, useQuery } from "@tanstack/react-query";
import Swal from "sweetalert2";
import UseToast from "utils/AfToast";
import Select from "react-select";
import Spinner from "utils/Spinner";
import { Button } from "@heroui/react";
import { useNavigate } from "react-router-dom";

const CnDnCreate: React.FC = () => {
  const navigate = useNavigate();

  const [noteType, setNoteType] = useState<"CN" | "DN">("CN");
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [selectedBranch, setSelectedBranch] = useState<any>(null);
  const [reason, setReason] = useState("");
  const [remarks, setRemarks] = useState("");
  const [selectedInvoices, setSelectedInvoices] = useState<any[]>([]);
  const [awbIdsText, setAwbIdsText] = useState("");

  // Options
  const [customerList, setCustomerList] = useState<any[]>([]);
  const [branchOptions, setBranchOptions] = useState<any[]>([]);
  const [invoiceOptions, setInvoiceOptions] = useState<any[]>([]);

  // Fetch customers
  useQuery(
    ["CustomersListApi_CnDnCreate"],
    () => ListCommanWay("/admin/customer/list", { isActive: true }, { paginate: 1000 }),
    {
      keepPreviousData: true,
      onSuccess(data) {
        if (data.status == "SUCCESS") {
          let opt: any[] = [];
          data.data.data.forEach((p: any) => {
            opt.push({ value: Number(p.id), label: p.code + " - " + p.sh });
          });
          setCustomerList(opt);
        }
      },
    }
  );

  // Fetch branches
  useQuery(
    ["BranchesListApi_CnDnCreate"],
    () => ListCommanWay("/admin/subbranch/list", {}, { paginate: 1000 }),
    {
      keepPreviousData: true,
      onSuccess(data) {
        if (data.status == "SUCCESS") {
          let opt: any[] = [];
          data.data.data.forEach((p: any) => {
            opt.push({ value: p.id, label: p.branch_name });
          });
          setBranchOptions(opt);
        }
      },
    }
  );

  // Fetch invoices for linking
  useQuery(
    ["InvoiceListApi_CnDnCreate"],
    () =>
      ListCommanWay(
        "/admin/inovic_for_c_b/list",
        { customerId: { $ne: "" } },
        { include: ["_customerId", "_subbranch"], paginate: 5000 }
      ),
    {
      keepPreviousData: true,
      onSuccess(data) {
        if (data.status == "SUCCESS") {
          let opt: any[] = [];
          data.data.data.forEach((p: any) => {
            opt.push({
              value: p.id,
              label: `${p._subbranch?.branch_code || ""}${p.invoice_no} - ${p._customerId?.sh || "N/A"} (₹${Number(p.total_value || 0).toFixed(2)})`,
            });
          });
          setInvoiceOptions(opt);
        }
      },
    }
  );

  // Create CN/DN mutation
  const { mutate: mutateCreate, isLoading: createLoading } = useMutation(
    (payload: any) =>
      CreateCommanWay({
        url: "/admin/cn-dn/create",
        data: payload,
      }),
    {
      onSuccess: (data: any) => {
        if (data.status == "SUCCESS") {
          const note = data.data;
          Swal.fire({
            title: `${note.note_type === "CN" ? "Credit" : "Debit"} Note Created`,
            html: `
              <div style="text-align: left;">
                <p><strong>Note No:</strong> ${note.note_no}</p>
                <p><strong>Total Value:</strong> ₹${Number(note.total_value).toFixed(2)}</p>
                <p><strong>IRN Status:</strong> <span style="color: ${note.irn_status === "ACTIVE" ? "green" : "#b8860b"}">${note.irn_status}</span></p>
              </div>
            `,
            icon: "success",
            confirmButtonText: "View Details",
            showCancelButton: true,
            cancelButtonText: "Back to List",
          }).then((result) => {
            if (result.isConfirmed) {
              navigate(`/billing/cn-dn/${note.id}`);
            } else {
              navigate("/billing/cn-dn");
            }
          });
        } else {
          UseToast(data.message || "Failed to create note", "error");
        }
      },
      onError: (data: any) => {
        typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
      },
    }
  );

  const handleSubmit = () => {
    if (!selectedBranch?.value) {
      UseToast("Please select a subbranch", "error");
      return;
    }
    if (!reason.trim()) {
      UseToast("Please enter a reason", "error");
      return;
    }
    if (!awbIdsText.trim()) {
      UseToast("Please enter AWB IDs", "error");
      return;
    }

    // Parse AWB IDs from comma-separated text
    const awbIds = awbIdsText
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0)
      .map((s) => {
        const num = Number(s);
        return isNaN(num) ? s : num;
      });

    if (awbIds.length === 0) {
      UseToast("No valid AWB IDs entered", "error");
      return;
    }

    const payload: any = {
      note_type: noteType,
      subbranch: selectedBranch.value,
      reason: reason.trim(),
      remarks: remarks.trim() || undefined,
      awb_ids: awbIds,
    };
    if (selectedCustomer?.value) {
      payload.customerId = selectedCustomer.value;
    }
    if (selectedInvoices.length > 0) {
      payload.invoice_ids = selectedInvoices.map((x: any) => x.value);
    }

    Swal.fire({
      title: `Create ${noteType === "CN" ? "Credit Note" : "Debit Note"}?`,
      html: `<p>This will create a ${noteType} with <strong>${awbIds.length}</strong> AWB(s).</p>`,
      icon: "question",
      confirmButtonText: "Create",
      showCancelButton: true,
    }).then((result) => {
      if (result.isConfirmed) {
        mutateCreate(payload);
      }
    });
  };

  return (
    <>
      {createLoading && <Spinner />}

      <h1 className="text-center my-3 text-lg font-semibold">Create Credit / Debit Note</h1>

      <div className="max-w-2xl mx-auto px-4">
        <div className="border rounded-lg p-6 bg-white shadow-sm">
          {/* Note Type Toggle */}
          <div className="mb-4">
            <label className="block mb-2 text-sm font-semibold text-gray-900">Note Type *</label>
            <div className="flex gap-4">
              <label
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 cursor-pointer transition-colors ${
                  noteType === "CN" ? "border-blue-500 bg-blue-50" : "border-gray-200"
                }`}
              >
                <input
                  type="radio"
                  name="noteType"
                  value="CN"
                  checked={noteType === "CN"}
                  onChange={() => setNoteType("CN")}
                />
                <span className="font-medium">Credit Note (CN)</span>
              </label>
              <label
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 cursor-pointer transition-colors ${
                  noteType === "DN" ? "border-orange-500 bg-orange-50" : "border-gray-200"
                }`}
              >
                <input
                  type="radio"
                  name="noteType"
                  value="DN"
                  checked={noteType === "DN"}
                  onChange={() => setNoteType("DN")}
                />
                <span className="font-medium">Debit Note (DN)</span>
              </label>
            </div>
          </div>

          {/* Customer */}
          <div className="mb-4">
            <label className="block mb-2 text-sm font-semibold text-gray-900">Customer (optional, leave blank for walk-in)</label>
            <Select
              className="bg-gray-50 border-gray-300 text-gray-900 text-sm rounded-lg block"
              options={customerList}
              onChange={(e: any) => setSelectedCustomer(e)}
              value={selectedCustomer}
              isClearable={true}
              placeholder="Select customer..."
            />
          </div>

          {/* Subbranch */}
          <div className="mb-4">
            <label className="block mb-2 text-sm font-semibold text-gray-900">Subbranch *</label>
            <Select
              className="bg-gray-50 border-gray-300 text-gray-900 text-sm rounded-lg block"
              options={branchOptions}
              onChange={(e: any) => setSelectedBranch(e)}
              value={selectedBranch}
              isClearable={false}
              placeholder="Select subbranch..."
            />
          </div>

          {/* Reason */}
          <div className="mb-4">
            <label className="block mb-2 text-sm font-semibold text-gray-900">Reason *</label>
            <input
              type="text"
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
              placeholder="e.g. Rate correction, Damaged goods"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>

          {/* Remarks */}
          <div className="mb-4">
            <label className="block mb-2 text-sm font-semibold text-gray-900">Remarks (optional)</label>
            <textarea
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
              rows={3}
              placeholder="Additional notes..."
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
            />
          </div>

          {/* Linked Invoices */}
          <div className="mb-4">
            <label className="block mb-2 text-sm font-semibold text-gray-900">Linked Invoices (optional)</label>
            <Select
              className="bg-gray-50 border-gray-300 text-gray-900 text-sm rounded-lg block"
              options={invoiceOptions}
              onChange={(e: any) => setSelectedInvoices(e || [])}
              value={selectedInvoices}
              isMulti={true}
              isClearable={true}
              placeholder="Search and select invoices..."
            />
          </div>

          {/* AWB IDs */}
          <div className="mb-6">
            <label className="block mb-2 text-sm font-semibold text-gray-900">AWB IDs * (comma-separated)</label>
            <textarea
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 font-mono"
              rows={3}
              placeholder="e.g. 10, 11, 12 or AHD1234567890, AHD0987654321"
              value={awbIdsText}
              onChange={(e) => setAwbIdsText(e.target.value)}
            />
            <p className="text-xs text-gray-500 mt-1">
              Enter AWB IDs separated by commas. Amounts will be auto-calculated from these AWBs.
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3 justify-end">
            <Button
              type="button"
              className="font-semibold font-inter"
              radius="sm"
              color="default"
              variant="bordered"
              onPress={() => navigate("/billing/cn-dn")}
            >
              Cancel
            </Button>
            <Button type="button" className="font-semibold font-inter text-white" radius="sm" color="primary" onPress={handleSubmit}>
              Create {noteType === "CN" ? "Credit Note" : "Debit Note"}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CnDnCreate;
